import {useContext, useEffect, useState} from "react";
import OrderContext from "../contexts/OrderContext.tsx";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Container,
    Paper, Grid2, Divider, Card, Stack, Link
} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import TextForm from "../components/TextForm.tsx";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {IOrder} from "../models/IOrder.ts";
import Button from "@mui/material/Button";
import {SupportedLanguages} from "../models/Languages.ts";
import i18n from "i18next";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AuthContext from "../contexts/AuthContext.tsx";

const OrderPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {t} = useTranslation()
    const {id: orderId} = useParams()
    const {
        order,
        errorFetchingOrder,
        setOrder,
        setOrderId,
        updateOrderMutation,
        errorUpdatingOrder,
        selectedCurrency,
        rates
    } = useContext(OrderContext)
    const [isEditing, setIsEditing] = useState<boolean>()
    const [errorMessage, setErrorMessage] = useState<string>()
    const {user} = useContext(AuthContext)
    const currentLanguage: SupportedLanguages = i18n.language as SupportedLanguages;
    const userLocale = navigator.language || 'en-US';
    const addressLabels = [`${t('NAME_LABEL_TEXT')}`, `${t('STREET_LABEL_TEXT')}`, `${t('ADDRESS2_LABEL_TEXT')}`, `${t('CITY_LABEL_TEXT')}`, `${t('REGION_LABEL_TEXT')}`, `${t('ZIP_CODE_LABEL_TEXT')}`]
    const addressFormData: any = {
        name: {value: order.shippingAddress.name, required: true},
        street: {value: order.shippingAddress.street, required: true},
        address2: {value: order.shippingAddress.address2, required: false},
        city: {value: order.shippingAddress.city, required: true},
        region: {value: order.shippingAddress.region, required: false},
        postalCode: {value: order.shippingAddress.postalCode, required: false},
    }

    const renderStars = (rating: number) => {
        return (
            <Typography component="span">
                {[1, 2, 3, 4, 5].map((i) => (
                    <span
                        key={i}
                        style={{
                            color: i <= rating ? "gold" : "lightgray",
                            fontSize: "20px",
                            marginRight: "2px",
                        }}
                    >
          ★
        </span>
                ))}
            </Typography>
        );
    };

    const orderEditable = order?.createdAt
        ? (new Date().getTime() - new Date(order.createdAt).getTime()) < 2 * 60 * 60 * 1000
        : false;

    useEffect(() => {
        if (orderId)
            setOrderId(orderId)
    }, [orderId])

    useEffect(() => {
        const handleRouteChange = () => {
            const nextPath = window.location.pathname;

            if (nextPath !== location.pathname) {
                setOrderId(undefined)
                sessionStorage.clear()
            }
        };
        return () => {
            handleRouteChange();
        };
    }, [location]);

    const getDHLTrackingLink = (trackingId: string) => {
        return `https://www.dhl.de/de/privatkunden.html?piececode=${trackingId}`
    }

    const handleAddressChange = (key: string, value: any) => {
        let newOrder: IOrder = order
        newOrder = {...newOrder, shippingAddress: {...newOrder.shippingAddress, [key]: value}}
        setOrder(newOrder)
    }

    const handleSubmit = () => {
        updateOrderMutation.mutate(order);
        if (errorUpdatingOrder) {
            setErrorMessage(errorUpdatingOrder)
        }
        setIsEditing(false)
    };

    const handleEditOrder = () => {
        setIsEditing(true)
    }


    if (errorFetchingOrder) {
        if (errorFetchingOrder.message.includes('404')) {
            navigate('/login', {state: {from: `/order/${orderId}`}})
        }
    }

    return (
        <Container sx={{padding: '12px'}}>
            <Paper elevation={4} sx={{paddingTop: 4, paddingBottom: 4, margin: 'auto', maxWidth: '900px'}}>
                <Container>
                    <Typography variant="h4" textAlign="left" gutterBottom>
                        {t('ORDER_PAGE_HEADER')}
                    </Typography>
                    <Typography variant="h5" textAlign="left" gutterBottom>
                        {t('CHECKOUT_PAGE_ADDRESS_TEXT')}
                    </Typography>
                </Container>
                {isEditing ? (
                    <TextForm formLabels={addressLabels} formData={addressFormData} onChange={handleAddressChange}
                              onSubmit={handleSubmit} submitText={t('SAVE_ORDER_BUTTON')}>
                    </TextForm>
                ) : (
                    <Box sx={{paddingLeft: 1, paddingRight: 3}}>
                        <Grid2 container spacing={2} sx={{ml: 2, mb: 3}}>
                            {Object.keys(addressFormData).map((key: string, index: number) => {
                                const {value} = addressFormData[key];
                                if (value === '')
                                    return
                                return (
                                    <Grid2
                                        key={key}
                                        size={{xs: 12, sm: 6}}
                                    >
                                        <Typography variant="h6" sx={{color: 'text.primary'}}>
                                            {addressLabels[index]}
                                        </Typography>
                                        <Typography sx={{color: 'text.secondary', fontSize: '1.1rem'}}>
                                            {value}
                                        </Typography>
                                    </Grid2>
                                );
                            })}
                        </Grid2>

                        <Divider sx={{my: 2}}/>
                        {/* Responsive Table */}
                        <Box sx={{display: {xs: 'none', md: 'block'}, overflowX: 'auto'}}>
                            <TableContainer component={Paper} sx={{borderRadius: 2, overflow: 'hidden'}}>
                                <Table sx={{minWidth: 600}}>
                                    <TableHead sx={{backgroundColor: '#f5f5f5'}}>
                                        <TableRow>
                                            <TableCell sx={{fontWeight: 'bold'}}>{t('PRODUCT_LABEL_TEXT')}</TableCell>
                                            <TableCell
                                                sx={{
                                                    fontWeight: 'bold',
                                                    textAlign: 'center'
                                                }}>{t('CART_PAGE_QUANTITY_TEXT')}</TableCell>
                                            <TableCell sx={{fontWeight: 'bold', textAlign: 'center'}}>
                                                {t('PRICE_LABEL_TEXT')}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontWeight: 'bold',
                                                textAlign: 'center'
                                            }}>{t('CART_PAGE_SUM_TEXT')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {order.items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    {currentLanguage === 'en' ? item.product.name.en : item.product.name.de}
                                                </TableCell>
                                                <TableCell sx={{textAlign: 'center'}}>
                                                    {item.quantity}
                                                </TableCell>
                                                <TableCell sx={{textAlign: 'center'}}>
                                                    {new Intl.NumberFormat(userLocale, {
                                                        style: 'currency',
                                                        currency: selectedCurrency,
                                                    }).format(item.price * rates[selectedCurrency])}</TableCell>
                                                <TableCell sx={{textAlign: 'center'}}>
                                                    {new Intl.NumberFormat(userLocale, {
                                                        style: 'currency',
                                                        currency: selectedCurrency,
                                                    }).format(item.price * item.quantity * rates[selectedCurrency])}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        {/* Mobile-friendly list view */}
                        <Box sx={{display: {paddingLeft: 2, xs: 'block', md: 'none'}}}>
                            {order.items.map((item, index) => (
                                <Card key={index} sx={{mb: 2, p: 2, borderRadius: 2}}>
                                    <Typography variant="subtitle1">
                                        {t('PRODUCT_LABEL_TEXT')}: {currentLanguage === 'en' ? item.product.name.en : item.product.name.de}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t('CART_PAGE_QUANTITY_TEXT')}: {item.quantity}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t('PRICE_LABEL_TEXT')}: {new Intl.NumberFormat(userLocale, {
                                        style: 'currency',
                                        currency: selectedCurrency,
                                    }).format(item.price * rates[selectedCurrency])}
                                    </Typography>
                                    <Typography variant="body2">
                                        {t('CART_PAGE_SUM_TEXT')}: {new Intl.NumberFormat(userLocale, {
                                        style: 'currency',
                                        currency: selectedCurrency,
                                    }).format(item.price * item.quantity * rates[selectedCurrency])}
                                    </Typography>
                                </Card>
                            ))}
                        </Box>

                        <Typography variant="h6" sx={{marginTop: 2, marginLeft: 2}}>
                            {t('SHIPPING_COST_LABEL_TEXT')}: {new Intl.NumberFormat(userLocale, {
                            style: 'currency',
                            currency: selectedCurrency,
                        }).format(order.shippingCost * rates[selectedCurrency])}
                        </Typography>
                        <Typography variant="h6" sx={{marginLeft: 2}}>
                            {t('PRICE_LABEL_TEXT')}: {new Intl.NumberFormat(userLocale, {
                            style: 'currency',
                            currency: selectedCurrency,
                        }).format(order.totalPrice * rates[selectedCurrency])}
                        </Typography>
                        <Typography variant="h5" sx={{marginTop: 2, textAlign: "left", marginLeft: 2., mb: 2}}>
                            {t('CART_PAGE_SUM_TEXT')}: {new Intl.NumberFormat(userLocale, {
                            style: 'currency',
                            currency: selectedCurrency,
                        }).format(order.shippingCost * rates[selectedCurrency] + order.totalPrice * rates[selectedCurrency])}
                        </Typography>
                        {order.isShipped ? (
                            <Stack direction="row" alignItems="center" spacing={1} sx={{mt: 1, mb: 2, ml: 2}}>
                                <LocalShippingIcon color="success"/>
                                <Typography variant="body2" sx={{fontWeight: 500, color: "green"}}>
                                    {t('STATE_SENT_TEXT')}
                                </Typography>
                                <Link
                                    href={getDHLTrackingLink(order.trackingId!)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ml: 1}}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Tracking ID: {order.trackingId}
                                </Link>
                            </Stack>
                        ) : (
                            <Typography variant="body2" sx={{mt: 1, mb: 2, ml: 2, fontWeight: 500, color: "orange"}}>
                                {t('STATE_NOT_SENT_TEXT')}
                            </Typography>
                        )}
                        <Stack direction="row" alignItems="center" sx={{mb: 2, ml: 2}}>
                            {!order.reviewed ?
                                <Box>
                                    {order.user === user!._id && <Button
                                        variant="outlined"
                                        color="primary"
                                        sx={{width: "100%"}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/create-review/${order._id}`);
                                        }}
                                    >
                                        {t('REVIEW_TEXT')}
                                    </Button>}
                                    <Button variant="contained"
                                            color="primary"
                                            fullWidth
                                            sx={{mt: 2}}
                                            disabled={!orderEditable}
                                            onClick={handleEditOrder}
                                    >
                                        {t('UPDATE_ORDER_BUTTON')}
                                    </Button>
                                    <Typography sx={{mt: 1}} variant="body2">
                                        *{t('EDITABLE_TIME_TEXT')}
                                    </Typography>
                                    <Typography color="error" sx={{marginBottom: '10px'}}>
                                        {errorMessage}
                                    </Typography>
                                </Box> :
                                <Box sx={{pt: 1, width: "100%"}}>
                                    <Typography
                                        variant="body2"
                                        sx={{display: 'flex', alignItems: 'center'}}
                                    >
                                        {t('REVIEW_WITH_TEXT')}{renderStars(order.reviewed.rating)}
                                    </Typography>

                                    {user?._id === order.reviewed.user &&
                                        <Button
                                            variant="outlined"
                                            sx={{mt: 1}}
                                            color="primary"
                                            fullWidth
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/edit-review/${order.reviewed._id}/of/${order._id}`);
                                            }}
                                        >
                                            {t("EDIT_REVIEW")}
                                        </Button>
                                    }
                                    <Button variant="contained"
                                            color="primary"
                                            fullWidth
                                            sx={{mt: 2}}
                                            disabled={!orderEditable}
                                            onClick={handleEditOrder}
                                    >
                                        {t('UPDATE_ORDER_BUTTON')}
                                    </Button>
                                    <Typography sx={{mt: 1}} variant="body2">
                                        *{t('EDITABLE_TIME_TEXT')}
                                    </Typography>
                                    <Typography color="error" sx={{marginBottom: '10px'}}>
                                        {errorMessage}
                                    </Typography>
                                </Box>
                            }
                        </Stack>

                    </Box>
                )}
            </Paper>
        </Container>
    )
}

export default OrderPage